import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Search, Tag, Package, AlertTriangle, CheckCircle2, X, Barcode
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapFiyatGorPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}` };

  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // Şu an gösterilen ürün
  const inputRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/products`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(setProducts)
      .catch(() => {});
    // Otomatik odaklan
    setTimeout(() => inputRef.current?.focus(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Barkod tam eşleşmesi veya isim/barkod arama
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    // Önce tam barkod eşleşmesi
    const exactBarcode = products.find(p => (p.barcode || '').trim() === query.trim());
    if (exactBarcode) return [exactBarcode];
    return products
      .filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.barcode || '').includes(q) ||
        (p.brand || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [products, query]);

  // Otomatik seçim: tek sonuç varsa göster
  useEffect(() => {
    if (results.length === 1) {
      setSelected(results[0]);
    } else if (results.length === 0) {
      setSelected(null);
    }
  }, [results]);

  // Barkod okuyucular Enter ile biter → otomatik ilk sonucu seç
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0) {
        setSelected(results[0]);
        if (results.length === 1) {
          // Barkod okuyucudan geldi büyük ihtimalle → temizle
          setTimeout(() => { setQuery(''); inputRef.current?.focus(); }, 3000);
        }
      } else {
        toast.error('Ürün bulunamadı');
      }
    } else if (e.key === 'Escape') {
      setQuery('');
      setSelected(null);
    }
  };

  const clear = () => { setQuery(''); setSelected(null); inputRef.current?.focus(); };

  const stockBadgeClass = (p) => {
    if (!p) return '';
    if (p.stock <= 0) return 'bg-red-500 text-white';
    if (p.stock <= p.min_stock) return 'bg-amber-500 text-white';
    return 'bg-emerald-500 text-white';
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="fiyat-gor-page">
      <div className="text-center">
        <div className="inline-flex items-center gap-2 mb-2">
          <Tag className="h-8 w-8 text-sky-600" />
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">Fiyat Gör</h1>
        </div>
        <p className="text-sm text-slate-500">Barkod okutun veya ürün adı yazın — fiyat ve stok anında görünsün</p>
      </div>

      {/* Büyük arama kutusu */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-slate-400 pointer-events-none" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Barkod okutun veya isim yazın..."
          className="pl-14 pr-12 h-16 text-xl rounded-2xl border-2 focus:border-sky-500"
          data-testid="fiyat-search-input"
          autoComplete="off"
        />
        {query && (
          <button
            onClick={clear}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            aria-label="Temizle"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Sonuç: Tek ürün büyük göster */}
      {selected ? (
        <Card className="border-2 border-sky-200 shadow-lg" data-testid="fiyat-result-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {selected.image_url && (
                <img src={selected.image_url} alt={selected.name}
                  className="w-full md:w-40 h-40 object-cover rounded-xl border" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    {selected.brand && <p className="text-sm text-slate-500 font-medium">{selected.brand}</p>}
                    <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 break-words">{selected.name}</h2>
                    {selected.barcode && (
                      <p className="text-xs text-slate-400 font-mono flex items-center gap-1 mt-1">
                        <Barcode className="h-3.5 w-3.5" />{selected.barcode}
                      </p>
                    )}
                    {selected.category && (
                      <span className="inline-block mt-2 px-3 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full text-xs font-medium">
                        {selected.category}
                      </span>
                    )}
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${stockBadgeClass(selected)}`}>
                    {selected.stock <= 0
                      ? <><AlertTriangle className="h-4 w-4" />Stokta Yok</>
                      : selected.stock <= selected.min_stock
                        ? <><AlertTriangle className="h-4 w-4" />Az Kaldı: {selected.stock} {selected.unit}</>
                        : <><CheckCircle2 className="h-4 w-4" />Stok: {selected.stock} {selected.unit}</>}
                  </span>
                </div>

                {/* Büyük fiyat */}
                <div className="mt-5 bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-6 text-white text-center">
                  <p className="text-sm uppercase tracking-wider opacity-90 mb-1">Satış Fiyatı</p>
                  <p className="text-5xl md:text-6xl font-black">
                    {(selected.sell_price || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}
                    <span className="text-2xl ml-2 font-bold">TL</span>
                  </p>
                  {selected.unit && selected.unit !== 'adet' && (
                    <p className="text-xs opacity-80 mt-1">/ {selected.unit}</p>
                  )}
                </div>

                {selected.description && (
                  <p className="mt-4 text-sm text-slate-600 dark:text-slate-400 line-clamp-3">{selected.description}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : results.length > 1 ? (
        // Birden çok sonuç: liste halinde göster
        <div className="space-y-2" data-testid="fiyat-multi-results">
          <p className="text-xs text-slate-500 mb-2">{results.length} ürün bulundu — birini seçin:</p>
          {results.map(p => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              className="w-full bg-white dark:bg-slate-900 border rounded-xl p-3 flex items-center gap-3 hover:border-sky-400 hover:shadow-md transition text-left"
              data-testid={`fiyat-result-${p.id}`}
            >
              {p.image_url ? (
                <img src={p.image_url} alt="" className="h-12 w-12 object-cover rounded border" loading="lazy" />
              ) : (
                <div className="h-12 w-12 bg-slate-100 dark:bg-slate-800 rounded flex items-center justify-center">
                  <Package className="h-6 w-6 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{p.name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {p.barcode && <span className="font-mono">{p.barcode} · </span>}
                  Stok: {p.stock} {p.unit}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold text-sky-600">{(p.sell_price || 0).toLocaleString('tr-TR')} TL</p>
              </div>
            </button>
          ))}
        </div>
      ) : query ? (
        <div className="text-center py-12 text-slate-400">
          <Package className="h-16 w-16 mx-auto mb-3 opacity-40" />
          <p className="text-lg">Ürün bulunamadı</p>
          <p className="text-xs mt-1">"{query}" için sonuç yok</p>
        </div>
      ) : (
        <div className="text-center py-12 text-slate-400">
          <Barcode className="h-20 w-20 mx-auto mb-3 opacity-30" />
          <p className="text-lg">Barkod okutmaya hazır</p>
          <p className="text-xs mt-2 max-w-md mx-auto">
            USB barkod okuyucu kullanıyorsanız direkt okutabilirsiniz.
            Klavyeden yazıp <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border rounded text-[10px]">Enter</kbd> ile de arayabilirsiniz.
          </p>
        </div>
      )}
    </div>
  );
}
